/** API contract types for the self-service portal user-management proxy. */

export type PortalUserSubscriptionTier =
  | 'free'
  | 'basic'
  | 'pro'
  | 'media_partner'
  | 'internal';

export type PortalUserStatus = 'active' | 'blocked';

export interface PortalSubscriptionUsage {
  gen_edit_used: number;
  gen_edit_limit: number | null;
  resize_used: number;
  resize_limit: number | null;
}

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  workspace_id: string | null;
  subscription: PortalUserSubscriptionTier;
  is_blocked: boolean;
  status: PortalUserStatus;
  monthly_used: number;
  monthly_limit: number;
  usage?: PortalSubscriptionUsage;
  creative_quota_override: number | null;
  pending_campaigns: number;
  created_at: string;
}

export interface PortalUsersPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PortalUser[];
}

export interface PortalUsageLimitConfig {
  monthly_creative_limit: number;
}

export interface PortalMetrics {
  new_users_30d: number;
  new_users_prior_30d: number;
  creatives_generated_30d: number;
  brands_ingested_30d: number;
}

export interface PortalMetricsViewModel {
  newUsersLast30d: number;
  newUsersDelta: string;
  newUsersDeltaDirection: 'up' | 'down' | 'neutral';
  creativesGeneratedLast30d: number;
  brandsIngestedLast30d: number;
}

export type StatusFilter = 'all' | PortalUserStatus;
export type SubscriptionFilter = 'all' | PortalUserSubscriptionTier;

export interface ListPortalUsersParams {
  search?: string;
  status?: PortalUserStatus;
  subscription?: PortalUserSubscriptionTier;
  page?: number;
  page_size?: number;
}

export interface AdminSubscription {
  workspace_id: string;
  workspace_name: string | null;
  tier: PortalUserSubscriptionTier;
  billing_cycle: 'none' | 'monthly' | 'annual';
  is_portal_managed: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  topup_credit_balance: number;
  usage: PortalSubscriptionUsage;
  tier_options: string[];
}

export interface AdminSubscriptionListResponse {
  results: AdminSubscription[];
  page: number;
  page_size: number;
}

export interface ListAdminSubscriptionsParams {
  tier?: PortalUserSubscriptionTier;
  page?: number;
  page_size?: number;
}

export const PORTAL_SYNC_NOT_CONFIGURED_ERROR_CODE = 'portal_sync_not_configured';

/** Tiers admins may set via the portal (paid tiers require Stripe Checkout). */
export const ADMIN_SETTABLE_SUBSCRIPTION_TIERS = [
  'free',
  'media_partner',
  'internal',
] as const satisfies readonly PortalUserSubscriptionTier[];

export type AdminSettableSubscriptionTier = (typeof ADMIN_SETTABLE_SUBSCRIPTION_TIERS)[number];
